import { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Paper,
    CircularProgress,
    Divider,
} from "@mui/material";
import bgImage from "../../assets/legalstream-meeting.avif";
import lsLogo from "../../assets/LegalStreamLogo.avif";
import { useTheme, useMediaQuery } from "@mui/material";
import {
    signIn,
    fetchAuthSession,
    getCurrentUser,
    signOut,
} from "@aws-amplify/auth";
import { ALLOWED_USER_GROUP } from "./awsConfig";
import LockIcon from "@mui/icons-material/Lock";
import EmailIcon from "@mui/icons-material/Email";
import KeyIcon from "@mui/icons-material/Key";
import AuthGate from "./AuthGate";
import { LsButton, LsTextField } from "../../core";

const MultiAuthGate = ({ children }) => {
    const theme = useTheme();
    const isSm = useMediaQuery(theme.breakpoints.down("sm"));

    const [onLoginSuccess, setOnLoginSuccess] = useState(false);
    const [authMethod, setAuthMethod] = useState(null);
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [checkingSession, setCheckingSession] = useState(true);

    useEffect(() => {
        const checkExistingSession = async () => {
            try {
                // Check for Duo token in cookies first
                const existingCookieToken = getCookie("access_token");

                if (existingCookieToken) {
                    console.log("Found Duo token in cookies");
                    setOnLoginSuccess(true);
                    setCheckingSession(false);
                    return;
                }

                // Check for Cognito session
                console.log("Checking Cognito session...");
                const user = await getCurrentUser();
                console.log("Found Cognito user:", user);

                await validateGroupAccess();
                setOnLoginSuccess(true);
            } catch (error) {
                console.log("No existing session found:", error.message);
            } finally {
                setCheckingSession(false);
            }
        };

        checkExistingSession();
    }, []); // Empty dependency - only run once on mount

    const getCookie = (name) => {
        const match = document.cookie.match(
            new RegExp("(^| )" + name + "=([^;]+)")
        );
        return match ? match[2] : null;
    };

    const handleLogin = async () => {
        setLoading(true);
        setError("");

        try {
            const result = await signIn({ username, password });
            console.log("Sign in result:", result);
            await validateGroupAccess();
            setOnLoginSuccess(true);
        } catch (err) {
            console.error("Login error:", err);
            setError(err.message || "Login failed");
            setLoading(false);
        }
    };

    const validateGroupAccess = async () => {
        try {
            const session = await fetchAuthSession();
            if (!session.tokens || !session.tokens.idToken) {
                throw new Error("No ID token found");
            }

            const token = session.tokens.idToken.payload;

            if (!token["cognito:groups"]?.includes(ALLOWED_USER_GROUP)) {
                await signOut({ global: true });
                throw new Error("Access Denied: Not Authorized User");
            }

            console.log("Group validation successful");
        } catch (err) {
            console.error("Group validation error:", err);
            throw err;
        }
    };

    const handleDuoLogin = async () => {
        setAuthMethod("duo");
    };

    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("code");

    // Show loading while checking session
    if (checkingSession) {
        return (
            <Box
                sx={{
                    minHeight: "100vh",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    background: `linear-gradient(135deg, #667eea 0%, #0c3c60 100%)`,
                }}
            >
                <CircularProgress sx={{ color: "white" }} />
            </Box>
        );
    }

    // If authenticated (by either method), render the app through AuthGate
    if (onLoginSuccess || authMethod === "duo" || code) {
        console.log("Rendering AuthGate, auth state:", { onLoginSuccess, authMethod, code });
        return <AuthGate>{children}</AuthGate>;
    }

    // Show login screen if not authenticated
    return (
        <Box
            sx={{
                minHeight: "100vh",
                position: "relative",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                background: `linear-gradient(135deg, #667eea 0%, #0c3c60 100%)`,
                backgroundImage: `url(${bgImage})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundAttachment: "fixed",
                "&::before": {
                    content: '""',
                    position: "absolute",
                    inset: 0,
                    bgcolor: "rgba(12, 60, 96, 0.65)", // Darker overlay for better contrast
                    zIndex: 0,
                },
            }}
        >
            {/* Centered Logo */}
            <Box
                sx={{
                    position: "fixed",
                    top: isSm ? 16 : 30,
                    left: isSm ? "50%" : "10%",
                    transform: "translateX(-50%)",
                    zIndex: 1000,
                    pointerEvents: "none",
                }}
            >
                <img
                    src={lsLogo}
                    alt="Logo"
                    style={{
                        height: isSm ? "100px" : "140px",
                        opacity: 0.95,
                        filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.3))",
                    }}
                />
            </Box>

            <Paper
                elevation={24}
                sx={{
                    position: "relative",
                    zIndex: 1,
                    width: "100%",
                    maxWidth: "440px",
                    mx: isSm ? 2 : 0,
                    p: isSm ? 3.5 : 5,
                    pt: isSm ? 18 : 6,
                    borderRadius: 4,
                    textAlign: "center",
                    backgroundColor: "#ffffff", // Solid white for better readability
                    backdropFilter: "blur(10px)",
                    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(255, 255, 255, 0.1)",
                }}
            >
                {/* Header */}
                <Box sx={{ mb: 4 }}>
                    <Typography
                        variant={isSm ? "h5" : "h4"}
                        sx={{ 
                            mb: 1, 
                            fontWeight: 700,
                            color: "#1a202c",
                            letterSpacing: "-0.02em",
                        }}
                    >
                        Welcome Back
                    </Typography>
                    <Typography 
                        variant="body2" 
                        sx={{ 
                            color: "text.secondary",
                            fontSize: "0.9375rem",
                        }}
                    >
                        Sign in to access your account
                    </Typography>
                </Box>

                {/* Login Form */}
                <Box
                    component="form"
                    sx={{ mb: 3 }}
                    onSubmit={(e) => {
                        e.preventDefault();
                        handleLogin();
                    }}
                >
                    <LsTextField
                        label="Email Address"
                        placeholder="user@legalstream.org"
                        fullWidth
                        value={username}
                        onChange={(e) => {
                            setUsername(e.target.value);
                            setError("");
                        }}
                        startAdornment={
                            <EmailIcon 
                                sx={{ 
                                    color: "action.active", 
                                    fontSize: "1.25rem" 
                                }} 
                            />
                        }
                        sx={{ mb: 2.5 }}
                    />
                    <LsTextField
                        type="password"
                        label="Password"
                        placeholder="enter your password"
                        fullWidth
                        value={password}
                        onChange={(e) => {
                            setPassword(e.target.value);
                            setError("");
                        }}
                        startAdornment={
                            <KeyIcon 
                                sx={{ 
                                    color: "action.active", 
                                    fontSize: "1.25rem" 
                                }} 
                            />
                        }
                        sx={{ mb: 3 }}
                    />
                    
                    <LsButton
                        variant="contained"
                        fullWidth
                        type="submit"
                        disabled={loading}
                        sx={{
                            py: 1.25,
                            fontSize: "0.9375rem",
                            fontWeight: 600,
                            textTransform: "none",
                            boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
                            "&:hover": {
                                boxShadow: "0 6px 16px rgba(25, 118, 210, 0.4)",
                            },
                        }}
                    >
                        {loading ? (
                            <>
                                <CircularProgress
                                    size={18}
                                    sx={{ mr: 1, color: "white" }}
                                />
                                Signing in...
                            </>
                        ) : (
                            "Sign In"
                        )}
                    </LsButton>
                </Box>

                {/* Divider */}
                <Box sx={{ my: 3, display: "flex", alignItems: "center" }}>
                    <Divider sx={{ flex: 1, borderColor: "rgba(0,0,0,0.08)" }} />
                    <Typography
                        variant="body2"
                        sx={{ 
                            px: 2, 
                            color: "text.secondary",
                            fontSize: "0.8125rem",
                            fontWeight: 500,
                        }}
                    >
                        OR
                    </Typography>
                    <Divider sx={{ flex: 1, borderColor: "rgba(0,0,0,0.08)" }} />
                </Box>

                {/* Duo Authentication Button */}
                <LsButton
                    variant="outlined"
                    fullWidth
                    onClick={handleDuoLogin}
                    disabled={loading}
                    startIcon={<LockIcon />}
                    sx={{
                        py: 1.25,
                        fontSize: "0.9375rem",
                        fontWeight: 600,
                        textTransform: "none",
                        borderWidth: "1.5px",
                        borderColor: "primary.main",
                        color: "primary.main",
                        "&:hover": {
                            borderWidth: "1.5px",
                            backgroundColor: "rgba(25, 118, 210, 0.04)",
                        },
                    }}
                >
                    Continue with Duo Security
                </LsButton>

                {error && (
                    <Box
                        sx={{
                            mt: 3,
                            p: 2,
                            borderRadius: 2,
                            backgroundColor: "#fef2f2",
                            border: "1px solid #fecaca",
                        }}
                    >
                        <Typography 
                            variant="body2"
                            sx={{ 
                                color: "#dc2626",
                                fontSize: "0.875rem",
                                fontWeight: 500,
                            }}
                        >
                            {error}
                        </Typography>
                    </Box>
                )}
            </Paper>
        </Box>
    );
};

export default MultiAuthGate;